import React, {useEffect} from "react";
import {
    FaArrowLeft,
    FaBell,
    FaChartLine,
    FaBullseye,
    FaSignal,
    FaUsers,
    FaChartBar,
    FaShieldHalved,
    FaTrophy,
    FaEnvelope,
    FaPhone,
} from "react-icons/fa6";
import {useContext} from "react";
import {SafeAreaContext} from "../../App";

const AboutPage = () => {
    const {setSafeAreaColor} = useContext(SafeAreaContext);

    useEffect(() => {
        setSafeAreaColor({background: "#FFFFFF"});

        document.documentElement.style.scrollBehavior = "smooth";

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.style.opacity = "1";
                        entry.target.style.transform = "translateY(0)";
                    }
                });
            },
            {threshold: 0.1, rootMargin: "0px 0px -50px 0px"},
        );

        document.querySelectorAll(".feature-card").forEach((card) => {
            card.style.opacity = "0";
            card.style.transform = "translateY(20px)";
            card.style.transition = "all 0.6s ease";
            observer.observe(card);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="bg-gray-50 font-sans min-h-screen">
            {/* Header */}
            <header className="bg-white px-4 py-4 fixed top-0 left-0 right-0 shadow-sm z-50">
                <div className="flex items-center justify-between max-w-md mx-auto">
                    <button
                        onClick={() => window.history.back()}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                        <FaArrowLeft className="text-gray-600"/>
                    </button>

                    <h1 className="text-xl font-bold text-gray-900">About Us</h1>

                    <div className="flex items-center gap-3">
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 hover:bg-orange-100 transition-colors">
                            <FaBell className="text-orange-500"/>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-12">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white py-16 px-5">
                    <div className="max-w-md mx-auto text-center">
                        <div
                            className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaChartLine className="text-4xl"/>
                        </div>
                        <h1 className="text-3xl font-bold mb-4">
                            Trading Signal Navigator
                        </h1>
                        <p className="text-lg opacity-90 leading-relaxed">
                            Your trusted companion in trading signals and financial markets
                        </p>
                    </div>
                </section>

                {/* Mission Section */}
                <section className="px-5 py-12 bg-white">
                    <div className="max-w-md mx-auto text-center">
                        <div
                            className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaBullseye className="text-3xl text-blue-600"/>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Our Mission
                        </h2>
                        <p className="text-gray-600 leading-relaxed max-w-sm mx-auto">
                            Democratizing trading with reliable, professional signals for
                            traders of all levels.
                        </p>
                    </div>
                </section>

                {/* Features Section */}
                <section className="px-5 py-12 bg-gray-50">
                    <div className="max-w-md mx-auto">
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">
                            What We Offer
                        </h2>

                        <div className="space-y-6">
                            <div className="feature-card bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <div className="flex gap-4">
                                    <div
                                        className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FaSignal className="text-white text-xl"/>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                                            Real-Time Signals
                                        </h3>
                                        <p className="text-gray-600 text-[15px] leading-relaxed">
                                            Instant alerts with precise entry, targets, and stop-loss
                                            levels.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="feature-card bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <div className="flex gap-4">
                                    <div
                                        className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FaUsers className="text-white text-xl"/>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                                            Expert Traders
                                        </h3>
                                        <p className="text-gray-600 text-[15px] leading-relaxed">
                                            Follow verified professionals with proven performance.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="feature-card bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <div className="flex gap-4">
                                    <div
                                        className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FaChartBar className="text-white text-xl"/>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                                            Market Analysis
                                        </h3>
                                        <p className="text-gray-600 text-[15px] leading-relaxed">
                                            In-depth insights across forex, crypto, stocks, and
                                            commodities.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="feature-card bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <div className="flex gap-4">
                                    <div
                                        className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <FaShieldHalved className="text-white text-xl"/>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                                            Risk Management
                                        </h3>
                                        <p className="text-gray-600 text-[15px] leading-relaxed">
                                            Tools and education for safe, responsible trading.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="px-5 py-12 bg-gray-900 text-white">
                    <div className="max-w-md mx-auto">
                        <h2 className="text-2xl font-bold text-center mb-10">Our Impact</h2>

                        <div className="grid grid-cols-2 gap-x-8 gap-y-10">
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FaUsers className="text-3xl text-blue-400"/>
                                </div>
                                <h3 className="text-4xl font-bold mb-1">50K+</h3>
                                <p className="text-gray-400">Active Traders</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FaChartLine className="text-3xl text-emerald-400"/>
                                </div>
                                <h3 className="text-4xl font-bold mb-1">85%</h3>
                                <p className="text-gray-400">Success Rate</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FaBullseye className="text-3xl text-amber-400"/>
                                </div>
                                <h3 className="text-4xl font-bold mb-1">1M+</h3>
                                <p className="text-gray-400">Signals Sent</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FaTrophy className="text-3xl text-sky-400"/>
                                </div>
                                <h3 className="text-4xl font-bold mb-1">200+</h3>
                                <p className="text-gray-400">Expert Traders</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="px-5 py-12 bg-white">
                    <div className="max-w-md mx-auto">
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">
                            Our Team
                        </h2>

                        <div className="space-y-6">
                            {[
                                {
                                    name: "Ahmad Rezaei",
                                    role: "CEO & Founder",
                                    desc: "15+ years in financial markets",
                                    img: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg",
                                },
                                {
                                    name: "Sara Ahmadi",
                                    role: "Head of Analysis",
                                    desc: "Technical analysis expert",
                                    img: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg",
                                },
                                {
                                    name: "Ali Hosseini",
                                    role: "Lead Developer",
                                    desc: "Platform architecture specialist",
                                    img: "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg",
                                },
                            ].map((member, i) => (
                                <div
                                    key={i}
                                    className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex gap-4"
                                >
                                    <img
                                        src={member.img}
                                        alt={member.name}
                                        className="w-16 h-16 rounded-2xl object-cover flex-shrink-0"
                                    />
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900">
                                            {member.name}
                                        </h3>
                                        <p className="text-blue-600 font-semibold">{member.role}</p>
                                        <p className="text-gray-600 text-sm mt-1.5">
                                            {member.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section className="px-5 py-12 bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
                    <div className="max-w-md mx-auto text-center">
                        <h2 className="text-2xl font-bold mb-4">Get In Touch</h2>
                        <p className="opacity-90 mb-8 text-base">
                            Questions or suggestions? We're here to help.
                        </p>

                        <div className="space-y-5 mb-10">
                            <div className="flex items-center justify-center gap-3 text-lg">
                                <FaEnvelope/>
                                <span>support@tradesignals.com</span>
                            </div>
                            <div className="flex items-center justify-center gap-3 text-lg">
                                <FaPhone/>
                                <span>021-12345678</span>
                            </div>
                        </div>

                        <button
                            className="bg-white text-blue-600 px-10 py-3.5 rounded-2xl font-bold hover:bg-gray-100 transition-all active:scale-95">
                            Contact Support
                        </button>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AboutPage;
